import React, { useState, useMemo } from 'react';
import { OrdenesTrabajoProvider, useOrdenesTrabajoContext } from '@/contexts/OrdenesTrabajoContext';
import { useObrasContext, type ObraJornada } from '@/contexts/ObrasContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSecurityContext } from '@/contexts/MaintenanceContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar, CalendarDays, ClipboardList, Clock, AlertTriangle, Plus, CheckCircle2, Sun, Trash2 } from 'lucide-react';
import { MiCalendarioSemana } from '@/components/ordenes/MiCalendarioSemana';
import { MiCalendarioHoy } from '@/components/ordenes/MiCalendarioHoy';
import { MiCalendarioMes } from '@/components/ordenes/MiCalendarioMes';
import { JornadaForm } from '@/components/jornadas/JornadaForm';
import { JornadaDocumentos } from '@/components/jornadas/JornadaDocumentos';
import { useAusencias, TIPO_AUSENCIA_LABEL, TIPO_AUSENCIA_COLOR } from '@/contexts/AusenciasContext';
import { isToday, isPast, format, parseISO, isWithinInterval } from 'date-fns';

const MiCalendarioContent: React.FC = () => {
  const { ordenes, loading } = useOrdenesTrabajoContext();
  const { obras, jornadas, deleteJornada } = useObrasContext();
  const { perfil } = useAuth();
  const { clientes } = useSecurityContext();
  const { ausencias } = useAusencias();
  const [activeView, setActiveView] = useState('hoy');
  const [showJornadaDialog, setShowJornadaDialog] = useState(false);
  const [nuevaJornadaId, setNuevaJornadaId] = useState<string | null>(null);
  const [jornadaEnEdicion, setJornadaEnEdicion] = useState<ObraJornada | null>(null);

  const obrasActivas = useMemo(
    () => obras.filter((o) => o.estado === 'activa'),
    [obras]
  );

  // Filter orders for the current user only (check both tecnico_asignado_id and tecnicos_asignados)
  const misOrdenes = useMemo(() => {
    if (!perfil?.user_id) return [];
    return ordenes.filter(orden => 
      orden.tecnico_asignado_id === perfil.user_id ||
      orden.tecnicos_asignados?.some(t => t.tecnico_id === perfil.user_id)
    );
  }, [ordenes, perfil?.user_id]);

  // Enrich orders with client names
  const ordenesEnriquecidas = useMemo(() => {
    return misOrdenes.map(orden => ({
      ...orden,
      cliente_nombre: clientes?.find(c => c.id === orden.cliente_id)?.nombre || 'Cliente desconocido'
    }));
  }, [misOrdenes, clientes]);

  // Jornadas of the current technician
  const misJornadas = useMemo(() => {
    if (!perfil?.user_id) return [];
    return jornadas.filter(j => j.tecnico_id === perfil.user_id);
  }, [jornadas, perfil?.user_id]);

  const jornadasRechazadas = useMemo(
    () => misJornadas.filter(j => j.estado_validacion === 'rechazada'),
    [misJornadas]
  );

  // Calculate statistics
  const stats = useMemo(() => {
    const activas = ordenesEnriquecidas.filter(o => 
      o.estado !== 'finalizada' && o.estado !== 'cancelada'
    );
    const hoy = ordenesEnriquecidas.filter(o => 
      o.fecha_limite && isToday(new Date(o.fecha_limite)) && 
      o.estado !== 'finalizada' && o.estado !== 'cancelada'
    );
    const enProgreso = ordenesEnriquecidas.filter(o => o.estado === 'en_progreso');
    const vencidas = ordenesEnriquecidas.filter(o => 
      o.fecha_limite && isPast(new Date(o.fecha_limite)) && !isToday(new Date(o.fecha_limite)) &&
      o.estado !== 'finalizada' && o.estado !== 'cancelada'
    );
    const urgentes = ordenesEnriquecidas.filter(o => 
      o.prioridad === 'urgente' && o.estado !== 'finalizada' && o.estado !== 'cancelada'
    );

    return { activas: activas.length, hoy: hoy.length, enProgreso: enProgreso.length, vencidas: vencidas.length, urgentes: urgentes.length };
  }, [ordenesEnriquecidas]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Cargando calendario...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-primary shrink-0" />
            <span className="truncate">Mi Calendario</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            Visualiza y gestiona tus órdenes de trabajo y jornadas
          </p>
        </div>
        <Button
          onClick={() => {
            setNuevaJornadaId(null);
            setShowJornadaDialog(true);
          }}
          className="gap-2 w-full sm:w-auto"
        >
          <Plus className="h-5 w-5" />
          Registrar Jornada
        </Button>
      </div>

      {/* Jornadas rechazadas */}
      {jornadasRechazadas.length > 0 && (
        <Card className="border-red-500/60 bg-red-500/5">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-400 font-semibold">
              <AlertTriangle className="h-4 w-4" />
              Jornadas rechazadas que requieren corrección ({jornadasRechazadas.length})
            </div>
            <div className="space-y-2">
              {jornadasRechazadas.map(j => (
                <div key={j.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-md border border-red-500/30 bg-card p-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {j.fecha} · {j.hora_inicio}{j.hora_fin ? `–${j.hora_fin}` : ''} · {j.horas_trabajadas?.toFixed(1) || '0'}h
                    </p>
                    {j.motivo_rechazo && (
                      <p className="text-xs text-red-700 dark:text-red-400 mt-0.5">
                        Motivo: {j.motivo_rechazo}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setJornadaEnEdicion(j);
                        setNuevaJornadaId(null);
                        setShowJornadaDialog(true);
                      }}
                    >
                      Corregir
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive border-destructive/40 hover:bg-destructive/10"
                      onClick={async () => {
                        if (!window.confirm('¿Eliminar esta jornada rechazada? Esta acción no se puede deshacer.')) return;
                        try {
                          await deleteJornada(j.id);
                        } catch {
                          // El error ya se muestra en el contexto
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Eliminar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mis ausencias próximas / activas */}
      {(() => {
        const misAus = ausencias.filter(
          (a) => a.tecnico_id === perfil?.user_id && a.estado_validacion !== "rechazada"
        );
        const hoy = new Date();
        const activasHoy = misAus.filter((a) =>
          isWithinInterval(hoy, { start: parseISO(a.fecha_inicio), end: parseISO(a.fecha_fin) })
        );
        const futuras = misAus
          .filter((a) => parseISO(a.fecha_inicio) > hoy)
          .sort((a, b) => a.fecha_inicio.localeCompare(b.fecha_inicio))
          .slice(0, 5);
        if (activasHoy.length === 0 && futuras.length === 0) return null;
        return (
          <Card className="border-2 border-blue-500/60 bg-blue-500/10">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 font-semibold text-blue-700 dark:text-blue-400 text-base">
                  <CalendarDays className="h-5 w-5" /> Mis ausencias
                </div>
                <div className="flex gap-2 text-xs">
                  {misAus.filter((a) => a.estado_validacion === "aprobada").length > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-green-600 text-white font-semibold">
                      ✓ {misAus.filter((a) => a.estado_validacion === "aprobada").length} aprobadas
                    </span>
                  )}
                  {misAus.filter((a) => a.estado_validacion === "pendiente").length > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-amber-500 text-white font-semibold">
                      ⏳ {misAus.filter((a) => a.estado_validacion === "pendiente").length} pendientes
                    </span>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                {[...activasHoy, ...futuras].map((a) => {
                  const aprobada = a.estado_validacion === "aprobada";
                  return (
                    <div
                      key={a.id}
                      className={`flex items-center gap-2 flex-wrap rounded-md border-l-4 p-3 text-sm bg-card ${
                        aprobada ? "border-l-green-500" : "border-l-amber-500"
                      }`}
                    >
                      <Badge className={TIPO_AUSENCIA_COLOR[a.tipo]}>{TIPO_AUSENCIA_LABEL[a.tipo]}</Badge>
                      <span className="font-semibold">
                        {format(parseISO(a.fecha_inicio), "dd/MM/yyyy")} – {format(parseISO(a.fecha_fin), "dd/MM/yyyy")}
                      </span>
                      <span className="text-xs text-muted-foreground">({Number(a.dias_solicitados)} días)</span>
                      {aprobada ? (
                        <Badge className="bg-green-600 text-white hover:bg-green-700">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> APROBADA
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-500 text-white hover:bg-amber-600">
                          <Clock className="h-3 w-3 mr-1" /> PENDIENTE DE VALIDACIÓN
                        </Badge>
                      )}
                      {activasHoy.includes(a) && (
                        <Badge variant="outline" className="border-blue-500 text-blue-700 dark:text-blue-300">
                          En curso
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Activas</p>
                <p className="text-xl sm:text-2xl font-bold">{stats.activas}</p>
              </div>
              <ClipboardList className="hidden sm:block h-8 w-8 text-blue-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Para Hoy</p>
                <p className="text-xl sm:text-2xl font-bold">{stats.hoy}</p>
              </div>
              <Calendar className="hidden sm:block h-8 w-8 text-cyan-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">En Progreso</p>
                <p className="text-xl sm:text-2xl font-bold">{stats.enProgreso}</p>
              </div>
              <Clock className="hidden sm:block h-8 w-8 text-purple-400 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className={stats.vencidas > 0 ? 'border-red-500/50' : ''}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Vencidas</p>
                <p className={`text-xl sm:text-2xl font-bold ${stats.vencidas > 0 ? 'text-red-400' : ''}`}>
                  {stats.vencidas}
                </p>
              </div>
              <AlertTriangle className={`hidden sm:block h-8 w-8 ${stats.vencidas > 0 ? 'text-red-400' : 'text-muted-foreground opacity-50'}`} />
            </div>
          </CardContent>
        </Card>

        <Card className={`col-span-2 md:col-span-1 ${stats.urgentes > 0 ? 'border-orange-500/50' : ''}`}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Urgentes</p>
                <p className={`text-xl sm:text-2xl font-bold ${stats.urgentes > 0 ? 'text-orange-400' : ''}`}>
                  {stats.urgentes}
                </p>
              </div>
              <Badge variant="outline" className={`${stats.urgentes > 0 ? 'border-orange-400 text-orange-400' : ''}`}>
                !
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Tabs */}
      <Tabs value={activeView} onValueChange={setActiveView} className="space-y-4">
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="hoy" className="gap-2">
            <Sun className="h-4 w-4" />
            Hoy
          </TabsTrigger>
          <TabsTrigger value="semana" className="gap-2">
            <CalendarDays className="h-4 w-4" />
            Semana
          </TabsTrigger>
          <TabsTrigger value="mes" className="gap-2">
            <Calendar className="h-4 w-4" />
            Mes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="hoy" className="mt-4">
          <MiCalendarioHoy ordenes={ordenesEnriquecidas} />
        </TabsContent>

        <TabsContent value="semana" className="mt-4">
          <MiCalendarioSemana
            ordenes={ordenesEnriquecidas}
            jornadas={misJornadas}
            ausencias={ausencias.filter(a => a.tecnico_id === perfil?.user_id)}
          />
        </TabsContent>

        <TabsContent value="mes" className="mt-4">
          <MiCalendarioMes
            ordenes={ordenesEnriquecidas}
            jornadas={misJornadas}
            ausencias={ausencias.filter(a => a.tecnico_id === perfil?.user_id)}
          />
        </TabsContent>
      </Tabs>

      {/* Empty state */}
      {ordenesEnriquecidas.length === 0 && !loading && (
        <Card className="mt-8">
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground">No tienes órdenes asignadas</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Las órdenes de trabajo que te asignen aparecerán aquí
            </p>
          </CardContent>
        </Card>
      )}

      {/* Dialog: registrar jornada + adjuntar foto */}
      <Dialog
        open={showJornadaDialog}
        onOpenChange={(open) => {
          setShowJornadaDialog(open);
          if (!open) {
            setNuevaJornadaId(null);
            setJornadaEnEdicion(null);
          }
        }}
      >
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>
              {nuevaJornadaId ? 'Adjuntar fotos a la jornada' : jornadaEnEdicion ? 'Corregir Jornada' : 'Registrar Jornada'}
            </DialogTitle>
          </DialogHeader>
          {!nuevaJornadaId ? (
            <JornadaForm
              obrasActivas={obrasActivas}
              jornada={jornadaEnEdicion ?? undefined}
              onSuccess={(id) => {
                if (jornadaEnEdicion) {
                  setShowJornadaDialog(false);
                  setJornadaEnEdicion(null);
                } else if (id) {
                  setNuevaJornadaId(id);
                } else {
                  setShowJornadaDialog(false);
                }
              }}
              onCancel={() => {
                setShowJornadaDialog(false);
                setJornadaEnEdicion(null);
              }}
            />
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg p-3 flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                Jornada guardada. Adjunta una foto si lo necesitas.
              </div>
              <JornadaDocumentos jornadaId={nuevaJornadaId} />
              <div className="flex justify-end">
                <Button
                  onClick={() => {
                    setShowJornadaDialog(false);
                    setNuevaJornadaId(null);
                  }}
                >
                  Finalizar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const MiCalendarioPage: React.FC = () => {
  return (
    <OrdenesTrabajoProvider>
      <MiCalendarioContent />
    </OrdenesTrabajoProvider>
  );
};

export default MiCalendarioPage;
